/**
 * Decides which screen the workspace home page should render.
 *
 * Extracted from `HomePage` so the branching logic can be unit-tested in
 * isolation and so the component itself stays simple. Returning `'loading'`
 * means the caller should render `null` (or a spinner) until queries settle.
 */
export type HomeScreen = 'loading' | 'builder-sandbox-home' | 'workspace-setup' | 'onboarding' | 'default';

export interface SelectHomeScreenInput {
  isBuilder: boolean;
  isSandbox: boolean;
  isTrial: boolean;
  isSetupGuideComplete: boolean;
  isSetupGuideLoading: boolean;
  isSetupGuideSuccess: boolean;
  /**
   * Essentials onboarding cohort: flag on + non-builder + (trial, or a paid promo account inside its
   * onboarding window), regardless of signup use case.
   */
  isEssentialsOnboardingEligible: boolean;
  isEssentialsOnboardingComplete: boolean;
  isEssentialsOnboardingLoading: boolean;
}

/** Minimal shape of the setup guide that this module needs. */
export interface SetupGuideLike {
  completed_at?: number | null;
}

/** Minimal shape of the setup guide query that this module needs. */
export interface SetupGuideQueryLike {
  data?: { setup_guide?: SetupGuideLike | null } | null;
  isLoading: boolean;
  isSuccess: boolean;
}

export function getWorkspaceIdOrDefault(workspaceId: number | null | undefined): number {
  return workspaceId ?? 0;
}

/** Returns the setup guide from the query payload, or `null` when absent. */
export function getSetupGuide<G extends SetupGuideLike>(query: { data?: { setup_guide?: G | null } | null }): G | null {
  return query.data?.setup_guide ?? null;
}

/** Returns true when the setup guide has a non-null `completed_at` timestamp. */
export function isSetupGuideCompleted(guide: SetupGuideLike | null): boolean {
  return guide?.completed_at != null;
}

export function selectHomeScreen(input: SelectHomeScreenInput): HomeScreen {
  const {
    isBuilder,
    isSandbox,
    isTrial,
    isSetupGuideComplete,
    isSetupGuideLoading,
    isSetupGuideSuccess,
    isEssentialsOnboardingEligible,
    isEssentialsOnboardingComplete,
    isEssentialsOnboardingLoading,
  } = input;

  if (isBuilder && isSandbox) {
    return 'builder-sandbox-home';
  }

  if (isBuilder) {
    return 'default';
  }

  // While the essentials gate's completion signals load, `isComplete` is
  // provisional (`useEssentialsOnboardingGate` contract) — hold rather than
  // render the checklist at an account that turns out to have finished, or
  // workspace-setup/default at one that has not. The gate only loads for
  // eligible accounts, so the flag is already cohort-scoped; it is not scoped
  // to trials because a paid promo account inside its onboarding window is
  // eligible too. Checked BEFORE the eligibility branch so it wins.
  if (isEssentialsOnboardingLoading) {
    return 'loading';
  }

  // Essentials onboarding cohort (SELF-138): the checklist replaces BOTH the
  // workspace-setup takeover and — once onboarding is complete in any
  // workspace — hands straight over to the standard home. This branch must
  // stay above the setup-guide branches or the incomplete setup guide would
  // win and the cohort would never see the checklist.
  if (isEssentialsOnboardingEligible) {
    if (!isEssentialsOnboardingComplete) {
      return 'onboarding';
    }
    return 'default';
  }

  // Trial accounts depend on the setup guide query.
  if (!isBuilder && isTrial && isSetupGuideLoading) {
    return 'loading';
  }

  if (!isBuilder && isTrial && isSetupGuideSuccess && !isSetupGuideComplete) {
    return 'workspace-setup';
  }

  return 'default';
}
